import { capitalizeSentence } from "../../utils/stringUtils";
import { Nullable } from "../../types/common";
import { SET_ERRORS } from "../../constants/types";

import store from "../store";

export interface IError {
  errorMessage: string;
  errorType: Nullable<string>;
  fieldErrors: Nullable<IFieldError[]>;
  status: number;
}

export interface IFieldError {
  errorMessage: string;
  fieldName: string;
}

export interface ICustomStatusMessages {
  [status: number]: {
    message: string;
    title?: string;
  };
}

export const handleError = (
  error: any,
  dispatch: any = store.dispatch,
  type: string = SET_ERRORS,
  customStatusMessages?: any
) => {
  console.error({ error });

  const payload = createError(error, customStatusMessages);

  dispatch({
    type,
    payload,
  });
};

const buildCustomErrorMessage = (
  status: number,
  customStatusMessages: ICustomStatusMessages
): IError => ({
  errorType: customStatusMessages[status].title ?? "",
  errorMessage: customStatusMessages[status].message,
  fieldErrors: null,
  status,
});

const buildErrorMessage = (
  status: number,
  errorMessage: string,
  errorType: string = "",
  fieldErrors: Nullable<IFieldError[]> = null,
  data?: any
): IError => {
  if (errorType === null) {
    if (data?.code) {
      errorType = data.code;
    } else {
      errorType = "unknown";
    }
  }

  if (errorType === "detail") {
    errorType = "";
  }

  errorMessage = capitalizeSentence(errorMessage);

  if (errorMessage.endsWith("not a valid choice.")) {
    errorMessage = `${capitalizeSentence(errorType)}: ${errorMessage}`;
  }

  return { errorType, errorMessage, fieldErrors, status };
};

const buildErrorMessageFromErrorObject = (
  status: number,
  data: any,
  defaultMessage: string
): IError => {
  if (status === 400) {
    return buildFormErrorMessage(status, data);
  }

  let errorType = Object.keys(data)[0];
  let errorMessage = defaultMessage;

  const errorValue = data[errorType];

  if (Array.isArray(errorValue)) {
    errorMessage = errorValue[0];
  } else if (typeof errorValue === "string") {
    errorMessage = errorValue;
  }

  if (errorMessage.toLowerCase() === "this field is required") {
    errorMessage = `'${errorType}' field is required.`;
    errorType = "missing-param";
  }

  return buildErrorMessage(status, errorMessage, errorType, null, data);
};

const buildFormErrorMessage = (status: number, data: any): IError => {
  const errorMessage = "The form contains errors, please review the highlighted fields.";
  const errorType = "FormError";

  if ("formless" in data) {
    return buildErrorMessage(status, data.name);
  }

  const fieldError = Object.keys(data).map((field) => ({
    fieldName: field,
    errorMessage: data[field],
  }));

  return buildErrorMessage(status, errorMessage, errorType, fieldError, data);
};

const isCustomErrorMessage = (status: number, customStatusMessages?: ICustomStatusMessages) =>
  customStatusMessages && customStatusMessages[status];

const isDjangoError = (data: any) => data.message && typeof data.message === "string";

export const createError = (error: any, customStatusMessages?: ICustomStatusMessages): IError => {
  let errorMessage = "Cannot make your request at this time.";

  const status = error.request?.status ?? 0;

  if (isCustomErrorMessage(status, customStatusMessages)) {
    return buildCustomErrorMessage(status, customStatusMessages!);
  }

  const data = error.response?.data;

  if (data && typeof data !== "string") {
    if (isDjangoError(data)) {
      return buildErrorMessage(status, data.message, data.code, null, data);
    }

    if (Object.keys(data)) {
      return buildErrorMessageFromErrorObject(status, data, errorMessage);
    }

    if (data.messages && Array.isArray(data.messages)) {
      errorMessage = data.messages[0].message;
    } else if (data.details && typeof data.details === "string") {
      errorMessage = data.details;
    }
  }

  return buildErrorMessage(status, errorMessage);
};

export const createEmptyError = (): IError => ({
  errorMessage: "",
  errorType: "",
  fieldErrors: null,
  status: 0,
});
